/**
 * Credentials list page.
 *
 * Replaces the old Vault page. Credentials are typed wrappers around
 * vault secrets — consumers (channels, ingress, http_sink) reference
 * them by id and never touch raw secret bytes.
 *
 * Filter by type via the toolbar. Test button on each row dispatches
 * a type-specific connectivity check (e.g. SMTP STARTTLS handshake,
 * Telegram getMe) and shows the result inline.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Key,
  Pencil,
  Plus,
  TestTube2,
  Trash2,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { CredentialDialog, type DialogMode } from "@/components/credentials/CredentialDialog";
import { CredentialTypeChooser } from "@/components/credentials/CredentialTypeChooser";
import { TagList } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  credentialsApi,
  type CredentialType,
} from "@/lib/api-credentials";
import { usePermissions } from "@/lib/permissions";


const TYPE_LABEL: Record<CredentialType, string> = {
  smtp: "SMTP",
  telegram_bot: "Telegram bot",
  http_bearer: "HTTP Bearer",
  api_key: "API Key",
  oauth2_client_credentials: "OAuth2 client credentials",
  postgres: "PostgreSQL",
  mysql: "MySQL / MariaDB",
  sqlserver: "SQL Server",
  tray_gateway: "Tray Gateway",
  hmac: "HMAC",
  sftp: "SFTP",
};


export function CredentialsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("credentials", "write");
  const canDelete = can("credentials", "delete");
  const canManage = canWrite || canDelete;

  const [dialog, setDialog] = React.useState<{ open: boolean; mode: DialogMode }>({
    open: false,
    mode: { kind: "create" },
  });
  const [chooserOpen, setChooserOpen] = React.useState(false);
  const [typeFilter, setTypeFilter] = React.useState<CredentialType | "">("");

  const listQ = useQuery({
    queryKey: ["credentials", { type: typeFilter || undefined }],
    queryFn: () =>
      credentialsApi.list({ type: typeFilter || undefined }),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      credentialsApi.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["credentials"] }),
  });

  const remove = useMutation({
    mutationFn: credentialsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["credentials"] }),
  });

  // The per-row Test mutation needs to remember which row produced
  // the latest result so we can render it inline beneath that row.
  const [testTarget, setTestTarget] = React.useState<string | null>(null);
  const test = useMutation({
    mutationFn: credentialsApi.test,
  });

  const rows = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("credentials.title", "Credentials")}
        description={t(
          "credentials.subtitle",
          "Typed credentials for outbound integrations. Secrets stay encrypted in the vault; consumers reference credentials by id.",
        )}
        actions={
          canWrite && (
            <Button
              size="sm"
              onClick={() => setChooserOpen(true)}
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("credentials.new", "New credential")}
            </Button>
          )
        }
      />
      <PageContent>
        {/* Type filter */}
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Type</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as CredentialType | "")}
            className="block rounded-md border border-border bg-background px-2 py-1"
          >
            <option value="">all</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        {rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Key}
            title={t("credentials.empty", "No credentials yet")}
            description={t(
              "credentials.emptyDescription",
              "Add SMTP servers, Telegram bots, DB connection strings, HTTP tokens — anywhere Tray needs to authenticate to an external system.",
            )}
            action={
              canWrite && (
                <Button onClick={() => setChooserOpen(true)}>
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  {t("credentials.new", "New credential")}
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-12">On</Th>
                  <Th>Name</Th>
                  <Th className="w-32">Type</Th>
                  <Th>Tags</Th>
                  <Th>Last used</Th>
                  {canManage && <Th className="w-40 text-right">Actions</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((c) => (
                  <React.Fragment key={c.id}>
                    <Tr>
                      <Td onClick={(e) => e.stopPropagation()}>
                        {canWrite ? (
                          <Switch
                            checked={c.enabled}
                            onCheckedChange={(v) =>
                              toggle.mutate({ id: c.id, enabled: v })
                            }
                          />
                        ) : (
                          <Badge tone={c.enabled ? "active" : "neutral"}>
                            {c.enabled ? "on" : "off"}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <div className="font-medium">{c.name}</div>
                        {c.description && (
                          <div className="text-xs text-muted-foreground">{c.description}</div>
                        )}
                      </Td>
                      <Td className="font-mono text-xs">{TYPE_LABEL[c.type] ?? c.type}</Td>
                      <Td>
                        <TagList tags={c.tags} max={3} />
                      </Td>
                      <Td className="text-xs text-muted-foreground">
                        {c.last_used_at ? (
                          <>
                            {new Date(c.last_used_at).toLocaleString()}
                            {c.last_used_for && (
                              <div className="font-mono text-[11px]">
                                by {c.last_used_for}
                              </div>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </Td>
                      {canManage && (
                        <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Test"
                              title="Test connectivity"
                              onClick={() => {
                                setTestTarget(c.id);
                                test.mutate(c.id);
                              }}
                            >
                              <TestTube2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Edit"
                                onClick={() =>
                                  setDialog({
                                    open: true,
                                    mode: { kind: "edit", credential: c },
                                  })
                                }
                              >
                                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Delete"
                                onClick={() => {
                                  if (confirm(`Delete credential "${c.name}"?`)) {
                                    remove.mutate(c.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </Td>
                      )}
                    </Tr>
                    {/* Inline test result row — only for the row that
                        produced the latest test response. */}
                    {testTarget === c.id && test.data && (
                      <Tr>
                        <Td colSpan={canManage ? 6 : 5} className="!py-0">
                          <Alert
                            tone={test.data.ok ? "info" : "danger"}
                            title={test.data.ok ? "Test passed" : "Test failed"}
                            className="my-2"
                          >
                            <div className="flex items-start gap-2 font-mono text-xs">
                              {test.data.ok ? (
                                <CheckCircle2
                                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success"
                                  strokeWidth={1.5}
                                />
                              ) : (
                                <AlertCircle
                                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger"
                                  strokeWidth={1.5}
                                />
                              )}
                              <span>{test.data.message}</span>
                            </div>
                          </Alert>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      <CredentialTypeChooser
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        onPick={(typeId) => {
          setChooserOpen(false);
          setDialog({ open: true, mode: { kind: "create", type: typeId } });
        }}
      />
      <CredentialDialog
        open={dialog.open}
        mode={dialog.mode}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onChangeType={() => {
          setDialog((d) => ({ ...d, open: false }));
          setChooserOpen(true);
        }}
      />
    </PageContainer>
  );
}
